/* eslint-disable */
import { ApolloError } from '@apollo/client';
import { Alert, Box, Card, Center, Skeleton, VStack } from '@chakra-ui/react';
import { useFocusedBranchContext } from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import useReservationGroupByGuest from '@client/page/management/table-reservation/analytic-new/logic/use-reservation-group-by-guest.ts';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useTranslate } from '@tolgee/react';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Dayjs } from 'dayjs';
import { Doughnut } from 'react-chartjs-2';

// Đăng ký plugin datalabels
import { ChartOptions, ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

function buildChartData(t: (value: string) => string, aggregateData: { guestCount: number | string; count: number }[]) {
  const sortedData = [...aggregateData].sort((a, b) => b.count - a.count);

  const labels = sortedData.map((item) => `${item.guestCount.toString()} ${t('seats')}`);
  const totalReservations = sortedData.reduce((acc, item) => acc + item.count, 0);
  const everydayTrends = sortedData.map((item) => (item.count / totalReservations) * 100);

  // Tạo màu động cho tất cả các nhóm
  const baseColors = [
    'rgba(75,130,192,0.8)',
    'rgba(255,99,132,0.8)',
    'rgba(255,206,86,0.8)',
    'rgba(54,162,235,0.8)',
    'rgba(153,102,255,0.8)',
    'rgba(255,159,64,0.8)',
    'rgba(199,199,199,0.8)',
    'rgba(83,102,255,0.8)',
    'rgba(255,205,86,0.8)',
    'rgba(75,192,192,0.8)',
  ];
  const backgroundColors = sortedData.map((_, idx) => baseColors[idx % baseColors.length]);
  const borderColors = sortedData.map((_, idx) => baseColors[idx % baseColors.length]?.replace('0.8', '1'));

  return {
    labels: labels,
    datasets: [
      {
        label: t('Percentages'),
        data: everydayTrends,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 2,
        borderRadius: 0,
        borderSkipped: false,
      },
    ],
  };
}

export default function ReservationGroupByGuest(props: { startTime: Dayjs; endTime: Dayjs }) {
  const { branch } = useFocusedBranchContext();

  const { t } = useTranslate();

  const { data, loading, error } = useReservationGroupByGuest(
    branch?.id,
    props.startTime.format(),
    props.endTime.format(),
  );

  if (loading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <Skeleton height="300px" />
      </VStack>
    );
  }

  if (error && error instanceof ApolloError) {
    return (
      <Alert.Root status="error" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Box>
          <Alert.Title>{t('Error loading guest distribution')}</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
        </Box>
      </Alert.Root>
    );
  }

  if (!data?.analytic_reservationsByGuestCount) {
    return null;
  }

  const aggregateData = data.analytic_reservationsByGuestCount;
  const chartData = buildChartData(t, aggregateData);

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.parsed;
            const percentage = value.toFixed(1);
            return `${label}: ${percentage}%`;
          },
        },
      },
      datalabels: {
        display: true,
        formatter: (value, context) => {
          const label = context.chart.data.labels?.[context.dataIndex];
          const percentage = value.toFixed(1);
          return `${label}: ${percentage}%`;
        },
        color: '#374151',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#d1d5db',
        borderWidth: 1,
        borderRadius: 4,
        padding: {
          top: 4,
          bottom: 4,
          left: 8,
          right: 8,
        },
        font: {
          weight: 'bold',
          size: 11,
        },
        textAlign: 'left',
        anchor: 'end',
        align: 'end',
        offset: 10,
        clip: false,
      },
    },
    elements: {
      arc: {
        borderRadius: 0,
      },
    },
    cutout: '50%',
    radius: '60%',
  };

  return (
    <Card.Root>
      <Card.Header p={0}>
        <Card.Title fontWeight={'semibold'}>{t('Seat Utilization Rate')}</Card.Title>
      </Card.Header>
      <Card.Body p={0}>
        <Center rounded={'xl'} mt={4} w="full" bgColor={'gray.50'} border={'1px solid'} borderColor={'gray.200'}>
          <Center style={{ width: '100%', height: '400px', position: 'relative' }}>
            <Doughnut options={options} data={chartData} />
          </Center>
        </Center>
      </Card.Body>
    </Card.Root>
  );
}
